import { useNotes, usePatchDocument, useReorderNotes } from "@mimir/api-client"
import { createFileRoute } from "@tanstack/react-router"
import { useState, useCallback, useMemo } from "react"
import { StickyNote, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { Note } from "@mimir/types"
import { DotBackground } from "@/components/backgrounds/dot-bg"
import { MasonryGrid } from "@/components/masonry-grid/masonry-grid"
import { InlineNoteCreator } from "@/components/notes/inline-note-creator"
import { NoteCard } from "@/components/notes/note-card"
import { NoteEditDialog } from "@/components/notes/note-edit-dialog"
import { motion } from "framer-motion"
import { PageShell } from "@/components/page-shell"
import { PageHeader } from "@/components/page-header"
import { EmptyState } from "@/components/empty-state"
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

export const Route = createFileRoute("/_app/notes")({
  component: NotesPage,
})

interface SortableNoteCardProps {
  note: Note
  isActive: boolean
  onOpen: (rect: DOMRect) => void
  onPinToggle: () => void
}

function SortableNoteCard({ note, isActive, onOpen, onPinToggle }: SortableNoteCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: note.id,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      className="cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <NoteCard note={note} isActive={isActive} onOpen={onOpen} onPinToggle={onPinToggle} />
    </div>
  )
}

function NotesPage() {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotes()
  const patchNote = usePatchDocument()
  const reorderNotes = useReorderNotes()
  const [active, setActive] = useState<{ note: Note; rect: DOMRect } | null>(null)
  const [dragState, setDragState] = useState<{ note: Note; width: number } | null>(null)
  const [localOrder, setLocalOrder] = useState<string[] | null>(null)

  const serverNotes: Note[] = useMemo(
    () => data?.pages.flatMap((p: { data: Note[] }) => p.data) ?? [],
    [data?.pages],
  )

  const notes: Note[] = useMemo(
    () =>
      localOrder
        ? localOrder.map((id) => serverNotes.find((n) => n.id === id)!).filter(Boolean)
        : serverNotes,
    [localOrder, serverNotes],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const note = notes.find((n) => n.id === event.active.id)
      const width = event.active.rect.current.initial?.width ?? 280
      setDragState(note ? { note, width } : null)
    },
    [notes],
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active: activeNote, over } = event
      if (!over || activeNote.id === over.id) return

      setLocalOrder((current) => {
        const base = current ?? serverNotes.map((n) => n.id)
        const oldIndex = base.indexOf(String(activeNote.id))
        const newIndex = base.indexOf(String(over.id))
        if (oldIndex === -1 || newIndex === -1) return current
        return arrayMove(base, oldIndex, newIndex)
      })
    },
    [serverNotes],
  )

  const handleDragEnd = useCallback(() => {
    setDragState(null)
    if (!localOrder) return

    reorderNotes.mutate(localOrder, {
      onError: () => setLocalOrder(null),
      onSuccess: () => setLocalOrder(null),
    })
  }, [localOrder, reorderNotes])

  const handleDragCancel = useCallback(() => {
    setDragState(null)
    setLocalOrder(null)
  }, [])

  return (
    <>
      <DotBackground vignette className="h-full absolute z-[-1]" />

      <PageShell className="min-w-[80%]">
        <PageHeader
          icon={StickyNote}
          title="Notes"
          subtitle="Quick thoughts and ephemeral memory"
        />

        <motion.div
          initial={{ opacity: 0, transform: "translateY(10px)" }}
          animate={{ opacity: 1, transform: "translateY(0)" }}
          transition={{ duration: 0.4, delay: 0.08 }}
        >
          <InlineNoteCreator />
        </motion.div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Fetching your notes...</p>
          </div>
        ) : isError ? (
          <EmptyState
            icon={StickyNote}
            title="Couldn't load notes"
            description="Something went wrong fetching your notes. Try refreshing the page."
          />
        ) : notes.length === 0 ? (
          <EmptyState
            icon={StickyNote}
            title="No notes yet"
            description="Capture quick thoughts, lists, or reminders. Mimir indexes them for you."
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={notes.map((n) => n.id)} strategy={rectSortingStrategy}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <MasonryGrid>
                  {notes.map((note) => (
                    <SortableNoteCard
                      key={note.id}
                      note={note}
                      isActive={active?.note.id === note.id}
                      onOpen={(rect) => setActive({ note, rect })}
                      onPinToggle={() =>
                        patchNote.mutate({ id: note.id, patch: { is_pinned: !note.is_pinned } })
                      }
                    />
                  ))}
                </MasonryGrid>
              </motion.div>
            </SortableContext>
            <DragOverlay>
              {dragState && (
                <div
                  style={{ width: dragState.width }}
                  className="opacity-90 rotate-1 scale-105 shadow-2xl cursor-grabbing"
                >
                  <NoteCard
                    note={dragState.note}
                    isActive={false}
                    onOpen={() => {}}
                    onPinToggle={() => {}}
                  />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}

        {hasNextPage && (
          <div className="flex justify-center pb-10">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="gap-2"
            >
              {isFetchingNextPage ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Load more notes"
              )}
            </Button>
          </div>
        )}
      </PageShell>

      {active && (
        <NoteEditDialog
          note={active.note}
          sourceRect={active.rect}
          onClose={() => setActive(null)}
        />
      )}
    </>
  )
}
